/**
 * focusDay.js — "Focus on one subject" plan built ONLY from existing data:
 * SYLLABUS outcomes (real curriculum codes + age bands), mastery.js evidence,
 * reuseEngine review schedule, TTQS/English engines. Stored in mwq_focus_v1
 * (additive). A focus can span several days. Nothing is locked.
 */
import { SYLLABUS } from "@/lib/syllabus";
import { matchesCurriculum } from "@/lib/canonical";
import { outcomesForSubject } from "@/lib/subjectAliases";
import { loadMastery } from "@/lib/mastery";
import { reviewDue } from "@/lib/reuseEngine";
import { GAMES } from "@/lib/gameCatalog";
import { ttqsState, englishState } from "@/lib/homeAdventure";

export const FOCUS_KEY = "mwq_focus_v1";
export const TIMES = [["10", "10 min"], ["30", "30 min"], ["60", "60 min"], ["90", "90 min"], ["half", "Half day"], ["full", "Full day"]];
export const yearToAge = (y) => Math.max(4, Math.min(15, Number(y) + 4));

const READY_KEY = "mwq_ready_codes_v1";
let readyCache = null;
/** Real practice readiness (static question bank) — never inferred from lesson availability. */
export async function fetchReadyCodes() {
  if (readyCache) return readyCache;
  try { const c = JSON.parse(localStorage.getItem(READY_KEY) || "null"); if (c && Date.now() - c.at < 864e5) { readyCache = new Set(c.codes); return readyCache; } } catch { /* ignore */ }
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/questions/ready-codes`);
    const d = await r.json();
    const codes = [...new Set(Object.values(d).flat())];
    readyCache = new Set(codes);
    localStorage.setItem(READY_KEY, JSON.stringify({ at: Date.now(), codes }));
  } catch { readyCache = new Set(); }
  return readyCache;
}
export const readyCodes = () => readyCache;
/** Distinct calendar days on which a step was completed (a focus may span several days). */
export function focusDays(focus) { const s = new Set(Object.values(focus?.doneAt || {}).map((t) => new Date(t).toDateString())); return Math.max(1, s.size); }

export function loadFocus() { try { return JSON.parse(localStorage.getItem(FOCUS_KEY) || "null"); } catch { return null; } }
export function saveFocus(f) { try { localStorage.setItem(FOCUS_KEY, JSON.stringify(f)); } catch { /* ignore */ } return f; }
export function clearFocus() { try { localStorage.removeItem(FOCUS_KEY); } catch { /* ignore */ } }

/** Verified outcomes for subject × year (age band) × optional curriculum. Never invents mappings. */
export function scopeFor(subject, year, curriculum = "") {
  const age = yearToAge(year);
  const all = outcomesForSubject(SYLLABUS, subject, age);
  const scoped = curriculum ? all.filter((o) => matchesCurriculum(o.curriculum, curriculum)) : all;
  const curricula = [...new Set(all.map((o) => o.curriculum))];
  return { outcomes: scoped, curricula, coverage: scoped.length === 0 ? "not_available" : scoped.length < 4 ? "partial" : "verified" };
}

/** Evidence state per outcome from the existing mastery store — no fabricated mastery. */
export function outcomeState(code, mastery = loadMastery(), due = reviewDue()) {
  const r = mastery[code];
  if (due.some((d) => d.code === code)) return "review";
  if (!r || !r.attempts?.length) return "not_assessed";
  if (r.mastered) return "mastered";
  const last = r.attempts.slice(-2);
  const good = last.every((a) => a.score / Math.max(1, a.total) >= 0.8);
  return good && last.length >= 2 ? "confident" : "practising";
}
export const STATE_META = {
  mastered: ["✓", "Mastered", "bg-emerald-100"], confident: ["★", "Developing · nearly secure", "bg-emerald-50"], practising: ["→", "Developing", "bg-sky-100"],
  review: ["↻", "Needs practice · review", "bg-amber-100"], not_assessed: ["○", "Not yet assessed", "bg-white"],
};

/** DIAGNOSE → LEARN → PRACTISE → RETRIEVE → APPLY → CHECK → REVIEW, sized to the time available. */
export function buildPlan(focus, ready = readyCache) {
  const { subject, year, curriculum, minutes, topics = [] } = focus;
  const practiceTo = (code) => (ready && ready.has(code) ? `/practice/${code}` : `/learn-topics/${code}/test`);
  const scope = scopeFor(subject, year, curriculum);
  const mastery = loadMastery(); const due = reviewDue();
  const chosen = topics.length ? scope.outcomes.filter((o) => topics.includes(o.code)) : scope.outcomes;
  const rank = { review: 0, practising: 1, not_assessed: 2, confident: 3, mastered: 4 };
  const ordered = [...chosen].sort((a, b) => rank[outcomeState(a.code, mastery, due)] - rank[outcomeState(b.code, mastery, due)]);
  const budget = { 10: 2, 30: 3, 60: 5, 90: 7, half: 10, full: 14 }[minutes] || 5;
  const targets = ordered.slice(0, Math.max(2, budget - 2));
  const game = GAMES.find((g) => !g.twoPlayer && (g.subjects || []).map((s) => String(s).toLowerCase()).includes(subject)) || null;
  const steps = [];
  if (String(minutes) !== "10") steps.push({ id: "diagnose", e: "🩺", stage: "Diagnose", title: `Quick ${subject} check`, sub: "A short adaptive check finds the best place to start.", to: `/diagnostic/${subject}`, minutes: 8 });
  targets.slice(0, Math.ceil(targets.length / 2)).forEach((o, i) => steps.push({ id: `learn-${o.code}`, e: "📚", stage: "Learn", title: o.outcome, sub: `${o.curriculum.toUpperCase()} · ${o.code}`, to: `/learn-topics?s=${subject}&y=${year}`, minutes: 12, code: o.code }));
  if (subject === "maths") { const t = ttqsState(); steps.push({ id: "ttqs", e: "🧮", stage: "Practise", title: t.line, sub: "Times Tables Quest · your real facts", to: t.to, minutes: 8 }); }
  if (subject === "english") { const en = englishState(); steps.push({ id: "english", e: "📖", stage: "Practise", title: en.line, sub: "English Quest", to: en.to, minutes: 10 }); }
  targets.slice(Math.ceil(targets.length / 2)).forEach((o) => steps.push({ id: `practise-${o.code}`, e: "🎯", stage: "Retrieve", title: o.outcome, sub: `${ready && ready.has(o.code) ? "Practice" : "Quick check"} · ${o.code}`, to: practiceTo(o.code), minutes: 8, code: o.code }));
  if (game) steps.push({ id: "apply", e: "🎮", stage: "Apply", title: game.label, sub: game.tagline, to: game.to, minutes: 6 });
  if (subject === "geography") steps.push({ id: "globe", e: "🌍", stage: "Apply", title: "Discover a country on the Globe", sub: "Facts · language · quiz · Passport stamp", to: "/globe", minutes: 10 });
  if (targets[0]) steps.push({ id: `check-${targets[0].code}`, e: "✅", stage: "Check", title: `Show what you know: ${targets[0].outcome}`, sub: "5 questions · evidence, not a test score", to: `/learn-topics/${targets[0].code}/test`, minutes: 6, code: targets[0].code });
  if (due.length) steps.push({ id: "review", e: "↻", stage: "Review", title: `Revisit ${due[0].code}`, sub: "Spaced review keeps it remembered", to: practiceTo(due[0].code), minutes: 5 });
  const cap = { 10: 4, 30: 6, 60: 9, 90: 11, half: 14, full: 18 }[minutes] || 9;
  return { scope, steps: steps.slice(0, cap), targets };
}

/** Parent summary from real evidence only. */
export function focusSummary(focus) {
  const { scope } = buildPlan(focus);
  const mastery = loadMastery(); const due = reviewDue();
  const counts = { mastered: 0, confident: 0, practising: 0, review: 0, not_assessed: 0 };
  let evidence = 0;
  for (const o of scope.outcomes) { const st = outcomeState(o.code, mastery, due); counts[st] += 1; evidence += (mastery[o.code]?.attempts || []).length; }
  const next = scope.outcomes.find((o) => ["review", "practising"].includes(outcomeState(o.code, mastery, due))) || scope.outcomes.find((o) => outcomeState(o.code, mastery, due) === "not_assessed");
  return { explored: scope.outcomes.length, evidence, ...counts, next: next ? next.outcome : null, coverage: scope.coverage };
}
